import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Network, Plus, Pencil, Trash2, Search, Loader2, ChevronRight,
  Globe, FolderOpen, ArrowLeft, X, Layers,
  MapPin, Server, Calculator, Lock, ListTree
} from 'lucide-react';
import { UsageBar, SubnetTreeList, SubnetDetailView } from './IPAMComponents';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { active: 'bg-emerald-500', reserved: 'bg-amber-500', dhcp: 'bg-blue-500', available: 'bg-slate-300', offline: 'bg-red-500' };
const STATUS_LABELS = { active: 'Active', reserved: 'Reserved', dhcp: 'DHCP', available: 'Available', offline: 'Offline' };

function VisualMap({ visualMap, onSelectIp }) {
  if (!visualMap || visualMap.length === 0) return null;
  const items = visualMap.slice(0, 1024);
  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex flex-wrap gap-[2px]">
        {items.map((ip) => (
          <Tooltip key={ip.ip}>
            <TooltipTrigger asChild>
              <button className={`w-4 h-4 rounded-sm ${STATUS_COLORS[ip.status] || 'bg-slate-200'} hover:ring-2 ring-indigo-400 transition-all`} onClick={() => onSelectIp(ip)} data-testid={`ip-cell-${ip.ip}`} />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p className="font-mono font-bold">{ip.ip}</p>
              <p>{STATUS_LABELS[ip.status]}</p>
              {ip.hostname && <p>{ip.hostname}</p>}
              {ip.customer_name && <p>{ip.customer_name}</p>}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
      {visualMap.length > 1024 && <p className="text-xs text-slate-400 mt-1">Showing 1024 of {visualMap.length}</p>}
      <div className="flex gap-3 mt-2 text-xs">
        {Object.entries(STATUS_COLORS).map(([s, c]) => (
          <span key={s} className="flex items-center gap-1"><span className={`w-3 h-3 rounded-sm ${c}`} />{STATUS_LABELS[s]}</span>
        ))}
      </div>
    </TooltipProvider>
  );
}


function CustomerSearch({ value, onChange, token }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (!search || search.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/wallet/search-customers?q=${encodeURIComponent(search)}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setResults(d.customers || []); }
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [search, token]);
  if (value && selected) {
    return (<div className="flex items-center gap-2 bg-slate-50 p-2 rounded-md"><span className="text-sm font-medium">{selected.company_name}</span><Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => { setSelected(null); onChange(''); setSearch(''); }}><X className="h-3 w-3" /></Button></div>);
  }
  return (
    <div>
      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customer..." className="text-sm" />
      {results.length > 0 && (<div className="border rounded-md mt-1 max-h-28 overflow-y-auto divide-y">{results.map(c => (<button key={c.customer_id} className="w-full text-left p-2 hover:bg-slate-50 text-xs" onClick={() => { setSelected(c); onChange(c.customer_id); setSearch(''); setResults([]); }}>{c.company_name} <span className="text-slate-400">{c.contact_phone}</span></button>))}</div>)}
    </div>
  );
}

export default function IPAM() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('sections');

  const [dashboard, setDashboard] = useState({});
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subnetAddresses, setSubnetAddresses] = useState(null);
  const [selectedSubnetDetail, setSelectedSubnetDetail] = useState(null);
  const [splitOptions, setSplitOptions] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [vlans, setVlans] = useState([]);
  const [freeIps, setFreeIps] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [racks, setRacks] = useState([]);
  const [selectedRack, setSelectedRack] = useState(null);
  const [calcResult, setCalcResult] = useState(null);
  const [calcInput, setCalcInput] = useState('');

  // Phase 3: Tree + Physical View
  const [sectionTree, setSectionTree] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({}); // {subnet_id: true}
  const [viewMode, setViewMode] = useState('tree'); // 'tree' | 'flat' (for sections list)
  const [physicalView, setPhysicalView] = useState(null);
  const [subnetView, setSubnetView] = useState('splits'); // 'splits' | 'physical' | 'spacemap'
  const [spaceMap, setSpaceMap] = useState(null);

  // Dialogs
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [showSubnetForm, setShowSubnetForm] = useState(false);
  const [editSubnet, setEditSubnet] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editAddress, setEditAddress] = useState(null);
  const [showSplitCreate, setShowSplitCreate] = useState(null);
  const [showVlanForm, setShowVlanForm] = useState(false);
  const [editVlan, setEditVlan] = useState(null);
  const [showAssignSubnet, setShowAssignSubnet] = useState(false);
  const [assignPrefix, setAssignPrefix] = useState('');
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [showRackForm, setShowRackForm] = useState(false);
  const [editRack, setEditRack] = useState(null);
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [editDevice, setEditDevice] = useState(null);

  // Forms
  const [sectionForm, setSectionForm] = useState({ name: '', description: '' });
  const [subnetForm, setSubnetForm] = useState({ section_id: '', subnet: '', description: '', gateway: '', nameservers: '', customer_id: '', location: '', note: '', is_pool: false, parent_subnet_id: '', vlan_id: '' });
  const [addressForm, setAddressForm] = useState({ subnet_id: '', ip_address: '', hostname: '', description: '', status: 'active', mac_address: '', customer_id: '', device: '', port: '', note: '' });
  const [splitForm, setSplitForm] = useState({ child_subnet: '', description: '', vlan_id: '', gateway: '', customer_id: '', location: '', is_pool: false });
  const [vlanForm, setVlanForm] = useState({ vlan_id: '', name: '', description: '', l2_domain: '', customer_id: '' });
  const [locationForm, setLocationForm] = useState({ name: '', description: '', address: '', city: '', state: '', country: 'India' });
  const [rackForm, setRackForm] = useState({ name: '', location_id: '', height: 42, description: '', row: '', serial_number: '' });
  const [deviceForm, setDeviceForm] = useState({ rack_id: '', name: '', u_start: '', u_size: 1, device_type: '', hostname: '', ip_address: '', serial_number: '', asset_tag: '', customer_id: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  // Fetchers
  const fetchDashboard = useCallback(async () => {
    try { const res = await fetch(`${API}/api/ipam/dashboard`, { headers }); if (res.ok) setDashboard(await res.json()); } catch {}
  }, [token]); // eslint-disable-line
  const fetchSections = useCallback(async () => {
    try { const res = await fetch(`${API}/api/ipam/sections`, { headers }); if (res.ok) { const d = await res.json(); setSections(d.sections || []); } } catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line
  const fetchSectionDetail = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/sections/${id}`, { headers }); if (res.ok) setSelectedSection(await res.json()); } catch {}
    fetchSectionTree(id);
  };
  const fetchSubnetAddresses = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/subnets/${id}/addresses`, { headers }); if (res.ok) setSubnetAddresses(await res.json()); } catch {}
  };
  const fetchSplitOptions = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/subnets/${id}/split-options`, { headers }); if (res.ok) setSplitOptions(await res.json()); } catch {}
  };
  const fetchSubnetDetail = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/subnets/${id}`, { headers }); if (res.ok) setSelectedSubnetDetail(await res.json()); } catch {}
  };
  const fetchFreeIps = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/subnets/${id}/free-space`, { headers }); if (res.ok) { const d = await res.json(); setFreeIps(d.free_ips || []); } } catch {}
  };
  const fetchVlans = async () => {
    try { const res = await fetch(`${API}/api/ipam/vlans`, { headers }); if (res.ok) { const d = await res.json(); setVlans(d.vlans || []); } } catch {}
  };
  const fetchLocations = async () => {
    try { const res = await fetch(`${API}/api/ipam/locations`, { headers }); if (res.ok) { const d = await res.json(); setLocations(d.locations || []); } } catch {}
  };
  const fetchLocationDetail = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/locations/${id}`, { headers }); if (res.ok) setSelectedLocation(await res.json()); } catch {}
  };
  const fetchRacks = async (locId) => {
    const q = locId ? `?location_id=${locId}` : '';
    try { const res = await fetch(`${API}/api/ipam/racks${q}`, { headers }); if (res.ok) { const d = await res.json(); setRacks(d.racks || []); } } catch {}
  };
  const fetchRackDetail = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/racks/${id}`, { headers }); if (res.ok) setSelectedRack(await res.json()); } catch {}
  };
  const fetchSectionTree = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/sections/${id}/tree`, { headers }); if (res.ok) setSectionTree(await res.json()); } catch {}
  };
  const fetchPhysicalView = async (id) => {
    try { const res = await fetch(`${API}/api/ipam/subnets/${id}/physical-view`, { headers }); if (res.ok) setPhysicalView(await res.json()); } catch {}
  };
  const fetchSpaceMap = async (id) => {
    setSpaceMap(null);
    try {
      const res = await fetch(`${API}/api/ipam/subnets/${id}/space-map?max_levels=8`, { headers });
      if (res.ok) setSpaceMap(await res.json());
    } catch {}
  };

  useEffect(() => { fetchSections(); fetchDashboard(); fetchVlans(); fetchLocations(); }, [fetchSections, fetchDashboard]);

  // Section handlers
  const handleSaveSection = async () => {
    if (!sectionForm.name.trim()) { toast.error('Name required'); return; }
    setSubmitting(true);
    const url = editSection ? `${API}/api/ipam/sections/${editSection.section_id}` : `${API}/api/ipam/sections`;
    try { const res = await fetch(url, { method: editSection ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(sectionForm) }); const d = await res.json(); if (res.ok) { toast.success(editSection ? 'Updated' : 'Created'); setShowSectionForm(false); setEditSection(null); fetchSections(); fetchDashboard(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleDeleteSection = async (id) => {
    if (!window.confirm('Delete section?')) return;
    try { const res = await fetch(`${API}/api/ipam/sections/${id}`, { method: 'DELETE', headers }); const d = await res.json(); if (res.ok) { toast.success('Deleted'); fetchSections(); fetchDashboard(); if (selectedSection?.section_id === id) setSelectedSection(null); } else toast.error(d.detail); } catch { toast.error('Error'); }
  };

  // Subnet handlers
  const handleSaveSubnet = async () => {
    if (!editSubnet && !subnetForm.subnet.trim()) { toast.error('Subnet CIDR required'); return; }
    setSubmitting(true);
    const url = editSubnet ? `${API}/api/ipam/subnets/${editSubnet.subnet_id}` : `${API}/api/ipam/subnets`;
    const payload = editSubnet ? { description: subnetForm.description, gateway: subnetForm.gateway, nameservers: subnetForm.nameservers, customer_id: subnetForm.customer_id || null, location: subnetForm.location, note: subnetForm.note, is_pool: subnetForm.is_pool, vlan_id: subnetForm.vlan_id ? parseInt(subnetForm.vlan_id) : null } : { ...subnetForm, vlan_id: subnetForm.vlan_id ? parseInt(subnetForm.vlan_id) : null };
    try { const res = await fetch(url, { method: editSubnet ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(payload) }); const d = await res.json(); if (res.ok) { toast.success(editSubnet ? 'Updated' : 'Created'); setShowSubnetForm(false); setEditSubnet(null); if (selectedSection) fetchSectionDetail(selectedSection.section_id); fetchDashboard(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleDeleteSubnet = async (id) => {
    if (!window.confirm('Delete subnet?')) return;
    try {
      const res = await fetch(`${API}/api/ipam/subnets/${id}`, { method: 'DELETE', headers });
      const d = await res.json();
      if (res.ok) {
        if (d.auto_unblocked_sibling) {
          toast.success(`Deleted. Auto-unblocked sibling ${d.auto_unblocked_sibling}`);
        } else {
          toast.success('Deleted');
        }
        if (selectedSection) { fetchSectionDetail(selectedSection.section_id); }
        if (selectedSubnetDetail?.subnet_id) { fetchSubnetDetail(selectedSubnetDetail.subnet_id); fetchSplitOptions(selectedSubnetDetail.subnet_id); fetchPhysicalView(selectedSubnetDetail.subnet_id); }
        fetchDashboard();
      } else {
        // Offer cascade delete when the reason is "has child subnets"
        const detail = String(d.detail || '');
        if (res.status === 400 && /child subnet/i.test(detail)) {
          const proceed = window.confirm(
            `${detail}\n\nDo you want to FORCE DELETE this subnet AND every subnet + IP address inside its range?\n\nThis is irreversible.`
          );
          if (proceed) {
            const confirmText = window.prompt('Type DELETE (all caps) to confirm cascade delete:');
            if (confirmText === 'DELETE') {
              const fres = await fetch(`${API}/api/ipam/subnets/${id}?force=true`, { method: 'DELETE', headers });
              const fd = await fres.json();
              if (fres.ok) {
                toast.success(`Force-deleted ${fd.deleted_subnets} subnets + ${fd.deleted_addresses} addresses`);
                if (selectedSection) { fetchSectionDetail(selectedSection.section_id); }
                setSelectedSubnetDetail(null); setSubnetAddresses(null);
                fetchDashboard();
                return;
              }
              toast.error(fd.detail || 'Force delete failed');
              return;
            } else if (confirmText !== null) {
              toast.error('Confirmation text did not match. Cancelled.');
              return;
            }
          }
        }
        toast.error(detail || 'Delete failed');
      }
    } catch { toast.error('Error'); }
  };

  const handleAssignSubnet = async (id) => {
    try {
      const res = await fetch(`${API}/api/ipam/subnets/${id}/assign`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({}) });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message || 'Assigned');
        if (selectedSection) fetchSectionDetail(selectedSection.section_id);
        if (selectedSubnetDetail?.subnet_id) fetchSubnetDetail(selectedSubnetDetail.subnet_id);
        fetchDashboard();
      } else toast.error(d.detail || 'Cannot assign');
    } catch { toast.error('Error'); }
  };

  const handleUnassignSubnet = async (id) => {
    if (!window.confirm('Unassign this subnet? It will become Free.')) return;
    try {
      const res = await fetch(`${API}/api/ipam/subnets/${id}/unassign`, { method: 'POST', headers });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message || 'Unassigned');
        if (selectedSection) fetchSectionDetail(selectedSection.section_id);
        if (selectedSubnetDetail?.subnet_id) fetchSubnetDetail(selectedSubnetDetail.subnet_id);
        fetchDashboard();
      } else toast.error(d.detail || 'Failed');
    } catch { toast.error('Error'); }
  };

  // Address handlers
  const handleSaveAddress = async () => {
    if (!editAddress && !addressForm.ip_address.trim()) { toast.error('IP required'); return; }
    setSubmitting(true);
    const url = editAddress ? `${API}/api/ipam/addresses/${editAddress.address_id}` : `${API}/api/ipam/addresses`;
    const payload = editAddress ? { hostname: addressForm.hostname, description: addressForm.description, status: addressForm.status, mac_address: addressForm.mac_address, customer_id: addressForm.customer_id || null, device: addressForm.device, port: addressForm.port, note: addressForm.note } : addressForm;
    try { const res = await fetch(url, { method: editAddress ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(payload) }); const d = await res.json(); if (res.ok) { toast.success(editAddress ? 'Updated' : 'IP added'); setShowAddressForm(false); setEditAddress(null); if (subnetAddresses) fetchSubnetAddresses(subnetAddresses.subnet.subnet_id); if (selectedSection) fetchSectionDetail(selectedSection.section_id); fetchDashboard(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Delete IP?')) return;
    try { const res = await fetch(`${API}/api/ipam/addresses/${id}`, { method: 'DELETE', headers }); if (res.ok) { toast.success('Deleted'); if (subnetAddresses) fetchSubnetAddresses(subnetAddresses.subnet.subnet_id); if (selectedSection) fetchSectionDetail(selectedSection.section_id); fetchDashboard(); } } catch { toast.error('Error'); }
  };

  // Split handler
  const handleCreateSplit = async () => {
    if (!showSplitCreate || !splitForm.child_subnet) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/ipam/subnets/${showSplitCreate.subnet_id}/split`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ ...splitForm, vlan_id: splitForm.vlan_id ? parseInt(splitForm.vlan_id) : null, customer_id: splitForm.customer_id || null })
      });
      const d = await res.json();
      if (res.ok) {
        if (d.subnet?.auto_blocked_sibling) {
          toast.success(`Subnet ${splitForm.child_subnet} assigned. Sibling ${d.subnet.auto_blocked_sibling} auto-blocked (/31 rule)`);
        } else {
          toast.success(`Subnet ${splitForm.child_subnet} assigned`);
        }
        setShowSplitCreate(null);
        if (selectedSection) fetchSectionDetail(selectedSection.section_id);
        // Refresh current subnet view
        const parentId = showSplitCreate.subnet_id;
        fetchSubnetAddresses(parentId);
        fetchSplitOptions(parentId);
        fetchSubnetDetail(parentId);
        fetchPhysicalView(parentId);
        fetchDashboard();
      }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Error'); }
    setSubmitting(false);
  };

  // VLAN handler
  const handleSaveVlan = async () => {
    if (!vlanForm.vlan_id || !vlanForm.name) { toast.error('VLAN ID and name required'); return; }
    setSubmitting(true);
    const url = editVlan ? `${API}/api/ipam/vlans/${editVlan.vlan_uuid}` : `${API}/api/ipam/vlans`;
    const payload = editVlan ? { name: vlanForm.name, description: vlanForm.description, l2_domain: vlanForm.l2_domain, customer_id: vlanForm.customer_id || null } : { ...vlanForm, vlan_id: parseInt(vlanForm.vlan_id), customer_id: vlanForm.customer_id || null };
    try { const res = await fetch(url, { method: editVlan ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(payload) }); const d = await res.json(); if (res.ok) { toast.success(editVlan ? 'Updated' : 'VLAN created'); setShowVlanForm(false); setEditVlan(null); fetchVlans(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleDeleteVlan = async (uuid) => {
    if (!window.confirm('Delete VLAN?')) return;
    try { const res = await fetch(`${API}/api/ipam/vlans/${uuid}`, { method: 'DELETE', headers }); const d = await res.json(); if (res.ok) { toast.success('Deleted'); fetchVlans(); } else toast.error(d.detail); } catch { toast.error('Error'); }
  };

  // Location handlers
  const handleSaveLocation = async () => {
    if (!locationForm.name.trim()) { toast.error('Name required'); return; }
    setSubmitting(true);
    const url = editLocation ? `${API}/api/ipam/locations/${editLocation.location_id}` : `${API}/api/ipam/locations`;
    try { const res = await fetch(url, { method: editLocation ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(locationForm) }); const d = await res.json(); if (res.ok) { toast.success(editLocation ? 'Updated' : 'Created'); setShowLocationForm(false); setEditLocation(null); fetchLocations(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Delete location?')) return;
    try { const res = await fetch(`${API}/api/ipam/locations/${id}`, { method: 'DELETE', headers }); const d = await res.json(); if (res.ok) { toast.success('Deleted'); fetchLocations(); setSelectedLocation(null); } else toast.error(d.detail); } catch { toast.error('Error'); }
  };

  // Rack handlers
  const handleSaveRack = async () => {
    if (!rackForm.name.trim()) { toast.error('Name required'); return; }
    setSubmitting(true);
    const url = editRack ? `${API}/api/ipam/racks/${editRack.rack_id}` : `${API}/api/ipam/racks`;
    const payload = editRack ? { name: rackForm.name, description: rackForm.description, height: parseInt(rackForm.height), row: rackForm.row, serial_number: rackForm.serial_number } : { ...rackForm, height: parseInt(rackForm.height) };
    try { const res = await fetch(url, { method: editRack ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(payload) }); const d = await res.json(); if (res.ok) { toast.success(editRack ? 'Updated' : 'Created'); setShowRackForm(false); setEditRack(null); if (selectedLocation) fetchLocationDetail(selectedLocation.location_id); fetchLocations(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleDeleteRack = async (id) => {
    if (!window.confirm('Delete rack?')) return;
    try { const res = await fetch(`${API}/api/ipam/racks/${id}`, { method: 'DELETE', headers }); const d = await res.json(); if (res.ok) { toast.success('Deleted'); if (selectedLocation) fetchLocationDetail(selectedLocation.location_id); fetchLocations(); setSelectedRack(null); } else toast.error(d.detail); } catch { toast.error('Error'); }
  };

  // Device handlers
  const handleSaveDevice = async () => {
    if (!deviceForm.name.trim() || !deviceForm.u_start) { toast.error('Name and U position required'); return; }
    setSubmitting(true);
    const url = editDevice ? `${API}/api/ipam/rack-devices/${editDevice.device_id}` : `${API}/api/ipam/rack-devices`;
    const payload = editDevice ? { name: deviceForm.name, u_start: parseInt(deviceForm.u_start), u_size: parseInt(deviceForm.u_size), device_type: deviceForm.device_type, hostname: deviceForm.hostname, ip_address: deviceForm.ip_address, serial_number: deviceForm.serial_number, asset_tag: deviceForm.asset_tag, customer_id: deviceForm.customer_id || null, description: deviceForm.description } : { ...deviceForm, u_start: parseInt(deviceForm.u_start), u_size: parseInt(deviceForm.u_size), customer_id: deviceForm.customer_id || null };
    try { const res = await fetch(url, { method: editDevice ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(payload) }); const d = await res.json(); if (res.ok) { toast.success(editDevice ? 'Updated' : 'Device added'); setShowDeviceForm(false); setEditDevice(null); if (selectedRack) fetchRackDetail(selectedRack.rack_id); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Error'); }
    setSubmitting(false);
  };
  const handleDeleteDevice = async (id) => {
    if (!window.confirm('Remove device?')) return;
    try { const res = await fetch(`${API}/api/ipam/rack-devices/${id}`, { method: 'DELETE', headers }); if (res.ok) { toast.success('Removed'); if (selectedRack) fetchRackDetail(selectedRack.rack_id); } } catch { toast.error('Error'); }
  };

  // Calculator
  const handleCalculate = async () => {
    if (!calcInput.trim()) return;
    try { const res = await fetch(`${API}/api/ipam/calculator?cidr=${encodeURIComponent(calcInput)}`, { headers }); const d = await res.json(); if (res.ok) setCalcResult(d); else toast.error(d.detail || 'Invalid'); } catch { toast.error('Error'); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try { const res = await fetch(`${API}/api/ipam/search?q=${encodeURIComponent(searchQuery)}`, { headers }); if (res.ok) { setSearchResults(await res.json()); setTab('search'); } } catch { toast.error('Error'); }
  };

  const openAddressForm = (subnetId, ip = '') => {
    setAddressForm({ subnet_id: subnetId, ip_address: ip, hostname: '', description: '', status: 'active', mac_address: '', customer_id: '', device: '', port: '', note: '' });
    setEditAddress(null);
    fetchFreeIps(subnetId);
    setShowAddressForm(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-4" data-testid="ipam-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Globe className="h-5 w-5 text-indigo-600" /> IP Address Management</h2>
          <p className="text-sm text-slate-500">{dashboard.subnets_count || 0} subnets | {dashboard.addresses_count || 0} addresses</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search IPs, hosts, subnets..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="pl-9 h-9" data-testid="ipam-search" />
          </div>
          <Button variant="outline" size="sm" onClick={handleSearch}><Search className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[{ v: dashboard.sections_count, l: 'Sections', c: 'text-slate-800' }, { v: dashboard.subnets_count, l: 'Subnets', c: 'text-indigo-600' }, { v: dashboard.addresses_count, l: 'IPs Managed', c: 'text-emerald-600' }, { v: dashboard.ipv4_subnets, l: 'IPv4', c: 'text-blue-600' }, { v: dashboard.ipv6_subnets, l: 'IPv6', c: 'text-purple-600' }].map((s, i) => (
          <Card key={i}><CardContent className="p-3 text-center"><div className={`text-2xl font-bold ${s.c}`}>{s.v || 0}</div><p className="text-xs text-slate-500">{s.l}</p></CardContent></Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={v => { setTab(v); if (v === 'sections') { setSelectedSection(null); setSubnetAddresses(null); setSplitOptions(null); setSelectedSubnetDetail(null); } }}>
        <TabsList className="bg-white border flex-wrap">
          <TabsTrigger value="sections"><FolderOpen className="h-4 w-4 mr-1" /> Sections</TabsTrigger>
          <TabsTrigger value="vlans"><Layers className="h-4 w-4 mr-1" /> VLANs</TabsTrigger>
          <TabsTrigger value="locations"><MapPin className="h-4 w-4 mr-1" /> Locations</TabsTrigger>
          <TabsTrigger value="racks"><Server className="h-4 w-4 mr-1" /> Racks</TabsTrigger>
          <TabsTrigger value="calculator"><Calculator className="h-4 w-4 mr-1" /> Calculator</TabsTrigger>
          <TabsTrigger value="search"><Search className="h-4 w-4 mr-1" /> Search</TabsTrigger>
        </TabsList>

        {/* SECTIONS TAB */}
        <TabsContent value="sections">
          {/* Level 1: Section List */}
          {!selectedSection && !subnetAddresses && (
            <div className="space-y-3">
              <div className="flex justify-end"><Button onClick={() => { setSectionForm({ name: '', description: '' }); setEditSection(null); setShowSectionForm(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-section-btn"><Plus className="h-4 w-4 mr-1" /> Add Section</Button></div>
              {sections.length === 0 ? (
                <Card><CardContent className="p-12 text-center text-slate-400"><FolderOpen className="h-16 w-16 mx-auto mb-4 opacity-30" /><p className="text-lg font-medium">No sections yet</p></CardContent></Card>
              ) : sections.map(s => (
                <Card key={s.section_id} className="hover:border-indigo-200 cursor-pointer transition-colors" data-testid={`section-${s.section_id}`}>
                  <CardContent className="p-4 flex items-center justify-between" onClick={() => { fetchSectionDetail(s.section_id); setSubnetAddresses(null); setSplitOptions(null); }}>
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2 bg-indigo-100 rounded-lg"><FolderOpen className="h-5 w-5 text-indigo-600" /></div>
                      <div><p className="font-semibold text-slate-800">{s.name}</p><p className="text-xs text-slate-500">{s.description || 'No description'} | {s.subnet_count || 0} subnets</p></div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setSectionForm({ name: s.name, description: s.description || '' }); setEditSection(s); setShowSectionForm(true); }}><Pencil className="h-4 w-4 text-amber-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeleteSection(s.section_id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Level 2: Section Detail - Subnets (Tree / Flat) */}
          {selectedSection && !subnetAddresses && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedSection(null); setSectionTree(null); }}><ArrowLeft className="h-4 w-4" /></Button>
                  <h3 className="text-lg font-semibold">{selectedSection.name}</h3>
                  <Badge variant="outline">{selectedSection.subnets?.length || 0} subnets</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex border rounded-md p-0.5 bg-slate-50">
                    <Button size="sm" variant={viewMode === 'tree' ? 'default' : 'ghost'} className={`h-7 text-xs ${viewMode === 'tree' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`} onClick={() => setViewMode('tree')} data-testid="tree-view-btn"><ListTree className="h-3 w-3 mr-1" />Tree</Button>
                    <Button size="sm" variant={viewMode === 'flat' ? 'default' : 'ghost'} className={`h-7 text-xs ${viewMode === 'flat' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`} onClick={() => setViewMode('flat')} data-testid="flat-view-btn"><Layers className="h-3 w-3 mr-1" />Flat</Button>
                  </div>
                  <Button onClick={() => { setSubnetForm({ section_id: selectedSection.section_id, subnet: '', description: '', gateway: '', nameservers: '', customer_id: '', location: '', note: '', is_pool: false, parent_subnet_id: '', vlan_id: '' }); setEditSubnet(null); setShowSubnetForm(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-subnet-btn"><Plus className="h-4 w-4 mr-1" /> Add Subnet</Button>
                </div>
              </div>

              {/* TREE VIEW */}
              {viewMode === 'tree' && (
                (!sectionTree || sectionTree.tree?.length === 0) ? (
                  <Card><CardContent className="p-12 text-center text-slate-400"><Network className="h-16 w-16 mx-auto mb-4 opacity-30" /><p>No subnets</p></CardContent></Card>
                ) : (
                  <Card>
                    <CardContent className="p-3">
                      <SubnetTreeList
                        tree={sectionTree.tree}
                        expanded={expandedNodes}
                        setExpanded={setExpandedNodes}
                        onOpen={(n) => { fetchSubnetAddresses(n.subnet_id); fetchSplitOptions(n.subnet_id); fetchSubnetDetail(n.subnet_id); fetchPhysicalView(n.subnet_id); setSpaceMap(null); setSubnetView('splits'); }}
                        onEdit={(n) => {
                          setEditSubnet(n);
                          setSubnetForm({ section_id: selectedSection.section_id, subnet: n.subnet, description: n.description || '', gateway: n.gateway || '', nameservers: n.nameservers || '', customer_id: n.customer_id || '', location: n.location || '', note: n.note || '', is_pool: n.is_pool || false, parent_subnet_id: n.parent_subnet_id || '', vlan_id: n.vlan_id ? String(n.vlan_id) : '' });
                          setShowSubnetForm(true);
                        }}
                        onDelete={(n) => handleDeleteSubnet(n.subnet_id)}
                      />
                    </CardContent>
                  </Card>
                )
              )}

              {/* FLAT VIEW */}
              {viewMode === 'flat' && ((!selectedSection.subnets || selectedSection.subnets.length === 0) ? (
                <Card><CardContent className="p-12 text-center text-slate-400"><Network className="h-16 w-16 mx-auto mb-4 opacity-30" /><p>No subnets</p></CardContent></Card>
              ) : selectedSection.subnets.map(sub => (
                <Card key={sub.subnet_id} className={`transition-colors ${sub.is_blocked ? 'border-red-200 bg-red-50/30' : 'hover:border-indigo-200'}`} data-testid={`subnet-${sub.subnet_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => { if (!sub.is_blocked) { fetchSubnetAddresses(sub.subnet_id); fetchSplitOptions(sub.subnet_id); fetchSubnetDetail(sub.subnet_id); fetchPhysicalView(sub.subnet_id); setSpaceMap(null); setSubnetView('splits'); } }}>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${sub.is_blocked ? 'bg-red-100' : 'bg-emerald-100'}`}>
                          {sub.is_blocked ? <Lock className="h-5 w-5 text-red-600" /> : <Network className="h-5 w-5 text-emerald-600" />}
                        </div>
                        <div>
                          <p className={`font-mono font-semibold ${sub.is_blocked ? 'text-red-700 line-through' : 'text-slate-800'}`}>{sub.subnet}</p>
                          <div className="text-xs text-slate-500 flex items-center flex-wrap gap-x-1">
                            {sub.version === 6 && <span className="text-purple-600 font-medium mr-1">IPv6</span>}
                            {(() => {
                              const ds = sub.display_status || 'free';
                              const m = {
                                assigned: 'bg-indigo-100 text-indigo-700 border-indigo-300',
                                autoblocked: 'bg-amber-100 text-amber-700 border-amber-300',
                                partial: 'bg-sky-100 text-sky-700 border-sky-300',
                                reserved: 'bg-slate-100 text-slate-600 border-slate-300',
                                free: 'bg-emerald-100 text-emerald-700 border-emerald-300',
                              };
                              const labels = { partial: 'Partially Allocated', free: 'Available', assigned: 'Allocated' };
                              const title = ds === 'autoblocked' ? 'Blocked by an overlapping assigned subnet (parent or child)' : ds === 'partial' ? 'Contains one or more allocated child subnets' : '';
                              return <Badge title={title} className={`text-[9px] mr-1 ${m[ds] || m.free}`} data-testid={`subnet-status-${sub.subnet_id}`}>{labels[ds] || (ds.charAt(0).toUpperCase() + ds.slice(1))}</Badge>;
                            })()}
                            <span>
                              {sub.description || 'No description'}
                              {sub.gateway ? ` | GW: ${sub.gateway}` : ''}
                              {sub.vlan_id ? ` | VLAN: ${sub.vlan_id}` : ''}
                              {sub.is_pool ? ' | Pool' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {sub.display_status === 'assigned' ? (
                          <Button variant="outline" size="sm" className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={e => { e.stopPropagation(); handleUnassignSubnet(sub.subnet_id); }} data-testid={`unassign-subnet-${sub.subnet_id}`}>Unassign</Button>
                        ) : sub.display_status === 'autoblocked' ? (
                          <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[10px]" data-testid={`autoblocked-badge-${sub.subnet_id}`}>Autoblocked</Badge>
                        ) : sub.display_status === 'partial' ? (
                          <Badge className="bg-sky-50 text-sky-600 border-sky-200 text-[10px]" title="Contains allocated child subnets — cannot be assigned as a whole" data-testid={`partial-badge-${sub.subnet_id}`}>Partially Allocated</Badge>
                        ) : (
                          <Button variant="outline" size="sm" className="text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={e => { e.stopPropagation(); handleAssignSubnet(sub.subnet_id); }} data-testid={`assign-subnet-${sub.subnet_id}`}><Plus className="h-3 w-3 mr-1" /> Assign</Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeleteSubnet(sub.subnet_id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        {!sub.is_blocked && <ChevronRight className="h-5 w-5 text-slate-400" />}
                      </div>
                    </div>
                    {!sub.is_blocked && <UsageBar usage={sub.usage} />}
                  </CardContent>
                </Card>
              )))}
            </div>
          )}

          {/* Level 3: Subnet Detail */}
          {subnetAddresses && (
            <SubnetDetailView
              subnetAddresses={subnetAddresses}
              subnetView={subnetView}
              setSubnetView={setSubnetView}
              physicalView={physicalView}
              fetchPhysicalView={fetchPhysicalView}
              splitOptions={splitOptions}
              selectedSubnetDetail={selectedSubnetDetail}
              fetchSubnetAddresses={fetchSubnetAddresses}
              fetchSplitOptions={fetchSplitOptions}
              fetchSubnetDetail={fetchSubnetDetail}
              setSplitForm={setSplitForm}
              setShowSplitCreate={setShowSplitCreate}
              setShowAssignSubnet={setShowAssignSubnet}
              setSubnetAddresses={setSubnetAddresses}
              setSelectedSubnetDetail={setSelectedSubnetDetail}
              handleDeleteSubnet={handleDeleteSubnet}
              spaceMap={spaceMap}
              fetchSpaceMap={fetchSpaceMap}
            />
          )}
        </TabsContent>

        {/* VLANS TAB */}
        <TabsContent value="vlans">
          <div className="space-y-3">
            <div className="flex justify-end"><Button onClick={() => { setVlanForm({ vlan_id: '', name: '', description: '', l2_domain: '', customer_id: '' }); setEditVlan(null); setShowVlanForm(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-vlan-btn"><Plus className="h-4 w-4 mr-1" /> Add VLAN</Button></div>
            {vlans.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-slate-400"><Layers className="h-16 w-16 mx-auto mb-4 opacity-30" /><p>No VLANs configured</p></CardContent></Card>
            ) : vlans.map(v => (
              <Card key={v.vlan_uuid}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg"><Layers className="h-5 w-5 text-blue-600" /></div>
                    <div>
                      <p className="font-semibold">VLAN {v.vlan_id} - {v.name}</p>
                      <p className="text-xs text-slate-500">
                        {v.description || 'No description'}
                        {v.l2_domain ? ` | L2: ${v.l2_domain}` : ''}
                        {' | '}{v.subnet_count || 0} subnets
                        {v.customer_name && <span className="text-indigo-600 font-medium"> | {v.customer_name}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => { setVlanForm({ vlan_id: String(v.vlan_id), name: v.name, description: v.description || '', l2_domain: v.l2_domain || '', customer_id: v.customer_id || '' }); setEditVlan(v); setShowVlanForm(true); }} data-testid={`assign-vlan-${v.vlan_uuid}`}><Plus className="h-3 w-3 mr-1" /> Assign</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteVlan(v.vlan_uuid)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>


        {/* LOCATIONS TAB */}
        <TabsContent value="locations">
          {!selectedLocation ? (
            <div className="space-y-3">
              <div className="flex justify-end"><Button onClick={() => { setLocationForm({ name: '', description: '', address: '', city: '', state: '', country: 'India' }); setEditLocation(null); setShowLocationForm(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-location-btn"><Plus className="h-4 w-4 mr-1" /> Add Location</Button></div>
              {locations.length === 0 ? (
                <Card><CardContent className="p-12 text-center text-slate-400"><MapPin className="h-16 w-16 mx-auto mb-4 opacity-30" /><p>No locations configured</p></CardContent></Card>
              ) : locations.map(loc => (
                <Card key={loc.location_id} className="hover:border-indigo-200 cursor-pointer transition-colors" onClick={() => fetchLocationDetail(loc.location_id)}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg"><MapPin className="h-5 w-5 text-orange-600" /></div>
                      <div><p className="font-semibold">{loc.name}</p><p className="text-xs text-slate-500">{[loc.address, loc.city, loc.state].filter(Boolean).join(', ') || 'No address'} | {loc.rack_count || 0} racks | {loc.subnet_count || 0} subnets</p></div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setLocationForm({ name: loc.name, description: loc.description || '', address: loc.address || '', city: loc.city || '', state: loc.state || '', country: loc.country || 'India' }); setEditLocation(loc); setShowLocationForm(true); }}><Pencil className="h-4 w-4 text-amber-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeleteLocation(loc.location_id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedLocation(null); setSelectedRack(null); }}><ArrowLeft className="h-4 w-4" /></Button>
                  <h3 className="text-lg font-semibold">{selectedLocation.name}</h3>
                  <Badge variant="outline">{selectedLocation.city}</Badge>
                </div>
                <Button onClick={() => { setRackForm({ name: '', location_id: selectedLocation.location_id, height: 42, description: '', row: '', serial_number: '' }); setEditRack(null); setShowRackForm(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-rack-btn"><Plus className="h-4 w-4 mr-1" /> Add Rack</Button>
              </div>
              {(!selectedLocation.racks || selectedLocation.racks.length === 0) ? (
                <Card><CardContent className="p-12 text-center text-slate-400"><Server className="h-16 w-16 mx-auto mb-4 opacity-30" /><p>No racks in this location</p></CardContent></Card>
              ) : selectedLocation.racks.map(rack => (
                <Card key={rack.rack_id} className="hover:border-indigo-200 cursor-pointer transition-colors" onClick={() => { fetchRackDetail(rack.rack_id); setTab('racks'); }}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg"><Server className="h-5 w-5 text-purple-600" /></div>
                      <div><p className="font-semibold">{rack.name}</p><p className="text-xs text-slate-500">{rack.height}U{rack.row ? ` | Row ${rack.row}` : ''}{rack.description ? ` | ${rack.description}` : ''}</p></div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeleteRack(rack.rack_id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* RACKS TAB - Visual Rack Diagram */}
        <TabsContent value="racks">
          {!selectedRack ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Select a rack from Locations tab, or view all racks below.</p>
              {locations.map(loc => (
                <div key={loc.location_id}>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">{loc.name}</h4>
                  {loc.rack_count === 0 && <p className="text-xs text-slate-400 mb-3">No racks</p>}
                </div>
              ))}
              <Button variant="outline" onClick={() => { fetchLocations(); setTab('locations'); }}>Go to Locations</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedRack(null)}><ArrowLeft className="h-4 w-4" /></Button>
                  <h3 className="text-lg font-semibold">{selectedRack.name}</h3>
                  <Badge variant="outline">{selectedRack.height}U</Badge>
                  <Badge variant="outline">{selectedRack.location_name}</Badge>
                </div>
                <Button onClick={() => { setDeviceForm({ rack_id: selectedRack.rack_id, name: '', u_start: '', u_size: 1, device_type: '', hostname: '', ip_address: '', serial_number: '', asset_tag: '', customer_id: '', description: '' }); setEditDevice(null); setShowDeviceForm(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-device-btn"><Plus className="h-4 w-4 mr-1" /> Add Device</Button>
              </div>

              {/* Visual Rack Diagram */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Rack Diagram</CardTitle></CardHeader>
                  <CardContent>
                    <div className="border rounded-lg bg-slate-900 p-2 font-mono text-xs">
                      {Array.from({ length: selectedRack.height }, (_, i) => {
                        const u = selectedRack.height - i;
                        const device = (selectedRack.devices || []).find(d => u >= d.u_start && u < d.u_start + d.u_size);
                        const isTop = device && u === device.u_start + device.u_size - 1;
                        const isBottom = device && u === device.u_start;
                        const colors = { server: 'bg-blue-700', switch: 'bg-emerald-700', router: 'bg-amber-700', patch_panel: 'bg-purple-700', ups: 'bg-red-800', pdu: 'bg-pink-800', other: 'bg-slate-600' };
                        const bg = device ? (colors[device.device_type] || 'bg-slate-600') : '';
                        return (
                          <div key={u} className={`flex items-center border-b border-slate-700 ${device ? bg + ' text-white' : 'text-slate-500'} ${isTop ? 'rounded-t' : ''} ${isBottom ? 'rounded-b' : ''}`}>
                            <span className="w-8 text-right pr-2 text-slate-500 text-[10px]">{u}</span>
                            <div className="flex-1 py-1 px-2 truncate">
                              {isTop && device && (
                                <span className="font-semibold">{device.name}{device.hostname ? ` (${device.hostname})` : ''}</span>
                              )}
                              {!device && <span className="text-slate-600">—</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Device List */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Devices ({(selectedRack.devices || []).length})</CardTitle></CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px]">
                      {(selectedRack.devices || []).length === 0 ? (
                        <div className="text-center py-8 text-slate-400"><p>No devices. Click "Add Device".</p></div>
                      ) : (selectedRack.devices || []).map(d => (
                        <div key={d.device_id} className="p-3 border-b hover:bg-slate-50" data-testid={`device-${d.device_id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-sm">{d.name}</p>
                              <p className="text-xs text-slate-500">
                                U{d.u_start}{d.u_size > 1 ? `-U${d.u_start + d.u_size - 1}` : ''} ({d.u_size}U)
                                {d.device_type ? ` | ${d.device_type}` : ''}
                                {d.hostname ? ` | ${d.hostname}` : ''}
                                {d.ip_address ? ` | ${d.ip_address}` : ''}
                              </p>
                              {d.customer_name && <p className="text-xs text-indigo-600">{d.customer_name}</p>}
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditDevice(d); setDeviceForm({ rack_id: d.rack_id, name: d.name, u_start: String(d.u_start), u_size: d.u_size, device_type: d.device_type || '', hostname: d.hostname || '', ip_address: d.ip_address || '', serial_number: d.serial_number || '', asset_tag: d.asset_tag || '', customer_id: d.customer_id || '', description: d.description || '' }); setShowDeviceForm(true); }}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteDevice(d.device_id)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* CALCULATOR TAB */}
        <TabsContent value="calculator">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calculator className="h-5 w-5" /> IPv4/IPv6 Subnet Calculator</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input value={calcInput} onChange={e => setCalcInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCalculate()} placeholder="Enter CIDR (e.g. 192.168.1.0/24 or 2001:db8::/32)" className="flex-1 font-mono" data-testid="calc-input" />
                <Button onClick={handleCalculate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="calc-btn"><Calculator className="h-4 w-4 mr-1" /> Calculate</Button>
              </div>
              <div className="flex gap-1 flex-wrap">
                <span className="text-xs text-slate-400 py-1">Quick:</span>
                {['10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16', '192.168.1.0/24', '2001:db8::/32', '2001:db8::/48', 'fd00::/48', '::1/128'].map(p => (
                  <Button key={p} variant="outline" size="sm" className="h-6 text-[10px] font-mono" onClick={() => { setCalcInput(p); }}>{p}</Button>
                ))}
              </div>
              {calcResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { l: 'Network', v: calcResult.network_address },
                      { l: 'Broadcast', v: calcResult.broadcast_address },
                      { l: 'Netmask', v: calcResult.netmask },
                      { l: 'Wildcard', v: calcResult.wildcard },
                      { l: 'CIDR', v: calcResult.cidr_notation },
                      { l: 'Prefix', v: `/${calcResult.prefix_length}` },
                      { l: 'Total Addresses', v: calcResult.total_addresses?.toLocaleString() },
                      { l: 'Usable Hosts', v: calcResult.usable_hosts?.toLocaleString() },
                      { l: 'First Usable', v: calcResult.first_usable },
                      { l: 'Last Usable', v: calcResult.last_usable },
                      { l: 'IP Version', v: `IPv${calcResult.version}` },
                      { l: 'Type', v: calcResult.is_private ? 'Private' : 'Public' },
                    ].map((item, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500">{item.l}</p>
                        <p className="font-mono font-semibold text-sm">{item.v}</p>
                      </div>
                    ))}
                  </div>
                  {calcResult.binary_netmask && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Binary Netmask</p>
                      <p className="font-mono text-sm">{calcResult.binary_netmask}</p>
                    </div>
                  )}
                  {calcResult.splits?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Split Options</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {calcResult.splits.map(s => (
                          <div key={s.prefix} className="border rounded-lg p-2 text-center">
                            <p className="font-mono font-bold text-indigo-600">/{s.prefix}</p>
                            <p className="text-xs text-slate-500">{s.count}x subnets</p>
                            <p className="text-xs">{s.usable} hosts each</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEARCH TAB */}
        <TabsContent value="search">
          {searchResults ? (
            <div className="space-y-4">
              <h3 className="text-base font-semibold">Results for "{searchQuery}"</h3>
              {searchResults.addresses.length > 0 && (
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">IP Addresses ({searchResults.addresses.length})</CardTitle></CardHeader><CardContent>
                  {searchResults.addresses.map(a => (
                    <div key={a.address_id} className="flex items-center justify-between p-2 border-b hover:bg-slate-50 text-sm">
                      <div><span className="font-mono font-medium">{a.ip_address}</span> <span className="text-slate-400">|</span> <span>{a.hostname || '-'}</span> <span className="text-slate-400">|</span> <span className="text-slate-500">{a.description || '-'}</span></div>
                      <div className="flex items-center gap-2"><Badge className={`${STATUS_COLORS[a.status]} text-white text-[10px]`}>{STATUS_LABELS[a.status]}</Badge><span className="text-xs text-slate-400 font-mono">{a.subnet_info?.subnet}</span></div>
                    </div>
                  ))}
                </CardContent></Card>
              )}
              {searchResults.subnets.length > 0 && (
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Subnets ({searchResults.subnets.length})</CardTitle></CardHeader><CardContent>
                  {searchResults.subnets.map(s => (
                    <div key={s.subnet_id} className="flex items-center justify-between p-2 border-b hover:bg-slate-50 text-sm cursor-pointer" onClick={() => { fetchSubnetAddresses(s.subnet_id); fetchSplitOptions(s.subnet_id); setTab('sections'); }}>
                      <div><span className="font-mono font-medium">{s.subnet}</span><span className="ml-2 text-slate-500">{s.description}</span></div>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  ))}
                </CardContent></Card>
              )}
              {searchResults.addresses.length === 0 && searchResults.subnets.length === 0 && (<Card><CardContent className="p-8 text-center text-slate-400"><p>No results</p></CardContent></Card>)}
            </div>
          ) : (<Card><CardContent className="p-8 text-center text-slate-400"><Search className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>Use search bar to find IPs, subnets, hostnames</p></CardContent></Card>)}
        </TabsContent>
      </Tabs>

      {/* Section Dialog */}
      <Dialog open={showSectionForm} onOpenChange={setShowSectionForm}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>{editSection ? 'Edit Section' : 'New Section'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name *</Label><Input value={sectionForm.name} onChange={e => setSectionForm(p => ({ ...p, name: e.target.value }))} data-testid="section-name" /></div>
            <div><Label>Description</Label><Textarea value={sectionForm.description} onChange={e => setSectionForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSectionForm(false)}>Cancel</Button><Button onClick={handleSaveSection} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-section-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subnet Dialog */}
      <Dialog open={showSubnetForm} onOpenChange={setShowSubnetForm}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>{editSubnet ? 'Edit Subnet' : 'New Subnet'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {!editSubnet && (
              <div>
                <Label>Subnet (CIDR) *</Label>
                <Input value={subnetForm.subnet} onChange={e => setSubnetForm(p => ({ ...p, subnet: e.target.value }))} placeholder="192.168.1.0/24 or 2001:db8::/48" data-testid="subnet-cidr" />
                <div className="flex gap-1 mt-2 flex-wrap">
                  <span className="text-xs text-slate-400 py-1">Quick:</span>
                  {['10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16', '2001:db8::/32', 'fd00::/48', 'fe80::/10'].map(p => (
                    <Button key={p} variant="outline" size="sm" className="h-6 text-[10px] font-mono" onClick={() => setSubnetForm(f => ({ ...f, subnet: p }))}>{p}</Button>
                  ))}
                </div>
              </div>
            )}
            <div><Label>Description</Label><Input value={subnetForm.description} onChange={e => setSubnetForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Gateway</Label><Input value={subnetForm.gateway} onChange={e => setSubnetForm(p => ({ ...p, gateway: e.target.value }))} placeholder="192.168.1.1" /></div>
              <div><Label>VLAN</Label>
                <Select value={subnetForm.vlan_id} onValueChange={v => setSubnetForm(p => ({ ...p, vlan_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="No VLAN" /></SelectTrigger>
                  <SelectContent><SelectItem value="_none">No VLAN</SelectItem>{vlans.map(v => <SelectItem key={v.vlan_id} value={String(v.vlan_id)}>VLAN {v.vlan_id} - {v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Nameservers</Label><Input value={subnetForm.nameservers} onChange={e => setSubnetForm(p => ({ ...p, nameservers: e.target.value }))} placeholder="8.8.8.8, 8.8.4.4" /></div>
            <div><Label>Location</Label><Input value={subnetForm.location} onChange={e => setSubnetForm(p => ({ ...p, location: e.target.value }))} /></div>
            <div><Label>Note</Label><Textarea value={subnetForm.note} onChange={e => setSubnetForm(p => ({ ...p, note: e.target.value }))} rows={2} /></div>
            <div className="flex items-center gap-2"><input type="checkbox" checked={subnetForm.is_pool} onChange={e => setSubnetForm(p => ({ ...p, is_pool: e.target.checked }))} id="is-pool" /><Label htmlFor="is-pool">Mark as IP Pool</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSubnetForm(false)}>Cancel</Button><Button onClick={handleSaveSubnet} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-subnet-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Address Dialog with Free IP List */}
      <Dialog open={showAddressForm} onOpenChange={v => { setShowAddressForm(v); if (!v) { setEditAddress(null); setFreeIps([]); } }}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>{editAddress ? `Edit ${editAddress.ip_address}` : 'Assign IP Address'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {!editAddress && (
              <div>
                <Label>IP Address *</Label>
                {freeIps.length > 0 ? (
                  <Select value={addressForm.ip_address} onValueChange={v => setAddressForm(p => ({ ...p, ip_address: v }))}>
                    <SelectTrigger data-testid="ip-address-select"><SelectValue placeholder="Select available IP" /></SelectTrigger>
                    <SelectContent>
                      {freeIps.map(ip => <SelectItem key={ip} value={ip} className="font-mono text-sm">{ip}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={addressForm.ip_address} onChange={e => setAddressForm(p => ({ ...p, ip_address: e.target.value }))} placeholder="e.g. 192.168.1.10" data-testid="ip-address-input" />
                )}
                {freeIps.length > 0 && <p className="text-xs text-emerald-600 mt-1">{freeIps.length} available IPs shown</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hostname</Label><Input value={addressForm.hostname} onChange={e => setAddressForm(p => ({ ...p, hostname: e.target.value }))} placeholder="server01" /></div>
              <div><Label>Status</Label>
                <Select value={addressForm.status} onValueChange={v => setAddressForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Input value={addressForm.description} onChange={e => setAddressForm(p => ({ ...p, description: e.target.value }))} placeholder="Web server, Router..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>MAC Address</Label><Input value={addressForm.mac_address} onChange={e => setAddressForm(p => ({ ...p, mac_address: e.target.value }))} placeholder="AA:BB:CC:DD:EE:FF" /></div>
              <div><Label>Device</Label><Input value={addressForm.device} onChange={e => setAddressForm(p => ({ ...p, device: e.target.value }))} /></div>
            </div>
            <div><Label>Port</Label><Input value={addressForm.port} onChange={e => setAddressForm(p => ({ ...p, port: e.target.value }))} placeholder="Gi0/1" /></div>
            <div><Label>Assign to Customer</Label><CustomerSearch value={addressForm.customer_id} onChange={v => setAddressForm(p => ({ ...p, customer_id: v }))} token={token} /></div>
            <div><Label>Note</Label><Textarea value={addressForm.note} onChange={e => setAddressForm(p => ({ ...p, note: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowAddressForm(false); setEditAddress(null); setFreeIps([]); }}>Cancel</Button><Button onClick={handleSaveAddress} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-address-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split / Assign Subnet Dialog */}
      <Dialog open={!!showSplitCreate} onOpenChange={() => setShowSplitCreate(null)}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Assign Subnet</DialogTitle><DialogDescription>Creating {splitForm.child_subnet} from {showSplitCreate?.subnet}</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Subnet</Label><Input value={splitForm.child_subnet} disabled className="font-mono bg-slate-50" /></div>
            <div><Label>Description / Label</Label><Input value={splitForm.description} onChange={e => setSplitForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Customer link, Server block" data-testid="split-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>VLAN</Label>
                <Select value={splitForm.vlan_id} onValueChange={v => setSplitForm(p => ({ ...p, vlan_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="No VLAN" /></SelectTrigger>
                  <SelectContent><SelectItem value="_none">No VLAN</SelectItem>{vlans.map(v => <SelectItem key={v.vlan_id} value={String(v.vlan_id)}>VLAN {v.vlan_id} - {v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Gateway</Label><Input value={splitForm.gateway} onChange={e => setSplitForm(p => ({ ...p, gateway: e.target.value }))} placeholder="e.g. x.x.x.1" /></div>
            </div>
            <div><Label>Assign to Customer</Label><CustomerSearch value={splitForm.customer_id} onChange={v => setSplitForm(p => ({ ...p, customer_id: v }))} token={token} /></div>
            <div><Label>Location</Label><Input value={splitForm.location || ''} onChange={e => setSplitForm(p => ({ ...p, location: e.target.value }))} placeholder="Site / POP" /></div>
            <div className="flex items-center gap-2"><input type="checkbox" checked={splitForm.is_pool} onChange={e => setSplitForm(p => ({ ...p, is_pool: e.target.checked }))} id="split-pool" /><Label htmlFor="split-pool">Mark as IP Pool</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSplitCreate(null)}>Cancel</Button><Button onClick={handleCreateSplit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-split-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VLAN Dialog */}
      <Dialog open={showVlanForm} onOpenChange={setShowVlanForm}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>{editVlan ? 'Edit / Assign VLAN' : 'New VLAN'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {!editVlan && <div><Label>VLAN ID *</Label><Input type="number" value={vlanForm.vlan_id} onChange={e => setVlanForm(p => ({ ...p, vlan_id: e.target.value }))} placeholder="1-4094" min="1" max="4094" data-testid="vlan-id" /></div>}
            <div><Label>Name *</Label><Input value={vlanForm.name} onChange={e => setVlanForm(p => ({ ...p, name: e.target.value }))} placeholder="VLAN name" data-testid="vlan-name" /></div>
            <div><Label>Description</Label><Input value={vlanForm.description} onChange={e => setVlanForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div><Label>L2 Domain</Label><Input value={vlanForm.l2_domain} onChange={e => setVlanForm(p => ({ ...p, l2_domain: e.target.value }))} placeholder="Default" /></div>
            <div><Label>Assign to Customer</Label><CustomerSearch value={vlanForm.customer_id} onChange={v => setVlanForm(p => ({ ...p, customer_id: v }))} token={token} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowVlanForm(false)}>Cancel</Button><Button onClick={handleSaveVlan} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-vlan-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Subnet Dialog - select from available list */}
      <Dialog open={showAssignSubnet} onOpenChange={v => { setShowAssignSubnet(v); if (!v) { setAssignPrefix(''); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Assign Subnet</DialogTitle>
            <DialogDescription>Select a prefix size, then pick an available subnet from {subnetAddresses?.subnet?.subnet}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 flex-1 overflow-hidden flex flex-col">
            <div>
              <Label>Select Prefix Size</Label>
              <Select value={assignPrefix} onValueChange={v => setAssignPrefix(v)}>
                <SelectTrigger data-testid="assign-prefix-select"><SelectValue placeholder="Choose /{prefix}..." /></SelectTrigger>
                <SelectContent>
                  {splitOptions?.options?.filter(o => o.available_count > 0 && o.count <= 256).map(opt => (
                    <SelectItem key={opt.prefix} value={String(opt.prefix)}>
                      /{opt.prefix} — {opt.label} ({opt.usable_per_subnet} hosts) — {opt.available_count} available
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {assignPrefix && (
              <div className="flex-1 overflow-hidden">
                <Label className="mb-1 block">Available Subnets</Label>
                <ScrollArea className="h-[340px] border rounded-lg">
                  <div className="divide-y">
                    {splitOptions?.options?.find(o => String(o.prefix) === assignPrefix)?.available?.map(sn => (
                      <button
                        key={sn}
                        className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors flex items-center justify-between group"
                        onClick={() => {
                          setSplitForm({ child_subnet: sn, description: '', vlan_id: '', gateway: '', customer_id: '', location: '', is_pool: false });
                          setShowSplitCreate(subnetAddresses.subnet);
                          setShowAssignSubnet(false);
                          setAssignPrefix('');
                        }}
                        data-testid={`assign-pick-${sn.replace(/[/.]/g, '-')}`}
                      >
                        <div>
                          <p className="font-mono font-semibold text-sm">{sn}</p>
                          <p className="text-xs text-slate-500">{splitOptions.options.find(o => String(o.prefix) === assignPrefix)?.usable_per_subnet} usable hosts</p>
                        </div>
                        <span className="text-xs text-emerald-600 opacity-0 group-hover:opacity-100 font-medium">Select</span>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowAssignSubnet(false); setAssignPrefix(''); }}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={showLocationForm} onOpenChange={setShowLocationForm}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>{editLocation ? 'Edit Location' : 'New Location'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name *</Label><Input value={locationForm.name} onChange={e => setLocationForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Bangalore DC" data-testid="location-name" /></div>
            <div><Label>Description</Label><Input value={locationForm.description} onChange={e => setLocationForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div><Label>Address</Label><Input value={locationForm.address} onChange={e => setLocationForm(p => ({ ...p, address: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label><Input value={locationForm.city} onChange={e => setLocationForm(p => ({ ...p, city: e.target.value }))} /></div>
              <div><Label>State</Label><Input value={locationForm.state} onChange={e => setLocationForm(p => ({ ...p, state: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowLocationForm(false)}>Cancel</Button><Button onClick={handleSaveLocation} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-location-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rack Dialog */}
      <Dialog open={showRackForm} onOpenChange={setShowRackForm}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>{editRack ? 'Edit Rack' : 'New Rack'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name *</Label><Input value={rackForm.name} onChange={e => setRackForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Rack-A1" data-testid="rack-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Height (U)</Label><Input type="number" value={rackForm.height} onChange={e => setRackForm(p => ({ ...p, height: e.target.value }))} min="1" max="60" /></div>
              <div><Label>Row</Label><Input value={rackForm.row} onChange={e => setRackForm(p => ({ ...p, row: e.target.value }))} placeholder="A" /></div>
            </div>
            <div><Label>Description</Label><Input value={rackForm.description} onChange={e => setRackForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div><Label>Serial Number</Label><Input value={rackForm.serial_number} onChange={e => setRackForm(p => ({ ...p, serial_number: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowRackForm(false)}>Cancel</Button><Button onClick={handleSaveRack} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-rack-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device Dialog */}
      <Dialog open={showDeviceForm} onOpenChange={v => { setShowDeviceForm(v); if (!v) setEditDevice(null); }}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>{editDevice ? 'Edit Device' : 'Add Device to Rack'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Device Name *</Label><Input value={deviceForm.name} onChange={e => setDeviceForm(p => ({ ...p, name: e.target.value }))} placeholder="Core Switch" data-testid="device-name" /></div>
              <div><Label>Type</Label>
                <Select value={deviceForm.device_type} onValueChange={v => setDeviceForm(p => ({ ...p, device_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {['server', 'switch', 'router', 'patch_panel', 'ups', 'pdu', 'other'].map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>U Start Position *</Label><Input type="number" value={deviceForm.u_start} onChange={e => setDeviceForm(p => ({ ...p, u_start: e.target.value }))} min="1" placeholder="1" data-testid="device-u-start" /></div>
              <div><Label>Size (U)</Label><Input type="number" value={deviceForm.u_size} onChange={e => setDeviceForm(p => ({ ...p, u_size: e.target.value }))} min="1" max="20" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hostname</Label><Input value={deviceForm.hostname} onChange={e => setDeviceForm(p => ({ ...p, hostname: e.target.value }))} /></div>
              <div><Label>IP Address</Label><Input value={deviceForm.ip_address} onChange={e => setDeviceForm(p => ({ ...p, ip_address: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Serial Number</Label><Input value={deviceForm.serial_number} onChange={e => setDeviceForm(p => ({ ...p, serial_number: e.target.value }))} /></div>
              <div><Label>Asset Tag</Label><Input value={deviceForm.asset_tag} onChange={e => setDeviceForm(p => ({ ...p, asset_tag: e.target.value }))} /></div>
            </div>
            <div><Label>Assign to Customer</Label><CustomerSearch value={deviceForm.customer_id} onChange={v => setDeviceForm(p => ({ ...p, customer_id: v }))} token={token} /></div>
            <div><Label>Description</Label><Input value={deviceForm.description} onChange={e => setDeviceForm(p => ({ ...p, description: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowDeviceForm(false); setEditDevice(null); }}>Cancel</Button><Button onClick={handleSaveDevice} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-device-btn">{submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}